import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { DataTable, ErrorState, Field, Modal, Pagination, Spinner, StatusBadge } from '../../components/ui.jsx';
import { api, toQuery } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useToast } from '../../state/ToastContext.jsx';
import { formatDate, formatNumber, formatXaf } from '../../lib/format.js';

const ModerationModal = ({ property, decision, onClose, onDone }) => {
  const toast = useToast();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const rejecting = decision === 'rejected';

  const submit = async (event) => {
    event.preventDefault();
    if (rejecting && !note.trim()) {
      toast.error('A rejection needs a reason so the landlord can fix it.');
      return;
    }

    setBusy(true);
    try {
      await api.patch(`/admin/properties/${property.id}/moderation`, {
        moderationStatus: decision,
        note: note.trim() || undefined
      });
      toast.success(rejecting ? 'Listing rejected and the landlord notified.' : 'Listing approved and now live.');
      onDone();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={rejecting ? 'Reject this listing' : 'Approve this listing'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="moderation-form" className={`btn btn-${rejecting ? 'danger' : 'primary'}`} disabled={busy}>
            {busy ? 'Saving...' : rejecting ? 'Reject' : 'Approve and publish'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        {rejecting
          ? 'The landlord sees your reason word for word and can edit and resubmit.'
          : 'The listing goes live immediately and tenants can start applying.'}
      </p>
      <form id="moderation-form" onSubmit={submit}>
        <Field
          label={rejecting ? 'Reason' : 'Note to the landlord'}
          hint={rejecting ? 'Be specific: what needs changing?' : 'Optional.'}
          required={rejecting}
        >
          <textarea
            rows="4"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={
              rejecting
                ? 'The photos are of a different house from the one described. Please upload pictures of the actual property.'
                : ''
            }
          />
        </Field>
      </form>
    </Modal>
  );
};

const AdminListings = () => {
  useTitle('Listing moderation');
  const [params, setParams] = useSearchParams();
  const moderation = params.get('moderation') || 'pending';
  const q = params.get('q') || '';
  const page = Number(params.get('page') || 1);

  const [search, setSearch] = useState(q);
  const [pending, setPending] = useState(null);

  const { data, loading, error, reload } = useFetch(`/admin/properties${toQuery({ moderation, q, page, pageSize: 15 })}`);

  const setParam = (patch) => setParams(new URLSearchParams(toQuery({ moderation, q, page: 1, ...patch }).replace(/^\?/, '')));

  const columns = [
    {
      key: 'title',
      header: 'Listing',
      render: (property) => (
        <div className="cell-listing">
          <Link to={`/properties/${property.id}`} className="cell-thumb">
            {property.coverImage ? <img src={property.coverImage} alt="" /> : <span>No photo</span>}
          </Link>
          <div>
            <Link to={`/properties/${property.id}`} className="cell-title">
              {property.title}
            </Link>
            <span className="muted">
              {[property.neighbourhood, property.city].filter(Boolean).join(', ')} - {property.rooms} room
              {property.rooms === 1 ? '' : 's'} - {property.mediaCount} files
            </span>
            {property.openReports > 0 ? (
              <span className="cell-warning">
                {property.openReports} open report{property.openReports === 1 ? '' : 's'}
              </span>
            ) : null}
            {property.moderationNote ? <span className="muted">Note: {property.moderationNote}</span> : null}
          </div>
        </div>
      )
    },
    {
      key: 'landlord',
      header: 'Landlord',
      render: (property) => (
        <div className="cell-stack">
          <Link to={`/landlords/${property.landlordId}`}>{property.landlordName}</Link>
          <span className="muted">{property.landlordEmail}</span>
          <StatusBadge status={property.landlordKyc} />
        </div>
      )
    },
    { key: 'price', header: 'Rent', render: (property) => formatXaf(property.price) },
    {
      key: 'state',
      header: 'State',
      render: (property) => (
        <div className="cell-stack">
          <StatusBadge status={property.moderationStatus} />
          <StatusBadge status={property.status} />
          <span className="muted">{formatNumber(property.viewCount)} views</span>
        </div>
      )
    },
    { key: 'created', header: 'Submitted', render: (property) => formatDate(property.createdAt) },
    {
      key: 'actions',
      header: 'Decision',
      render: (property) => (
        <div className="cell-actions">
          {property.moderationStatus !== 'approved' ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setPending({ property, decision: 'approved' })}
            >
              Approve
            </button>
          ) : null}
          {property.moderationStatus !== 'rejected' ? (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => setPending({ property, decision: 'rejected' })}
            >
              Reject
            </button>
          ) : null}
          <Link to={`/properties/${property.id}`} className="btn btn-ghost btn-sm">
            Inspect
          </Link>
        </div>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Listing moderation</h1>
          <p className="muted">
            Check that the photos match the description, the rent is plausible and the town is right before publishing.
          </p>
        </div>
      </header>

      <div className="filter-bar">
        <div className="tab-row">
          {[
            ['pending', 'Waiting for review'],
            ['approved', 'Approved'],
            ['rejected', 'Rejected'],
            ['any', 'All']
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`tab ${moderation === value ? 'is-current' : ''}`}
              onClick={() => setParam({ moderation: value })}
            >
              {label}
            </button>
          ))}
        </div>

        <form
          className="search-inline"
          onSubmit={(event) => {
            event.preventDefault();
            setParam({ q: search.trim() });
          }}
        >
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Title, town or landlord"
          />
          <button type="submit" className="btn btn-outline btn-sm">
            Search
          </button>
        </form>
      </div>

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}

      {data ? (
        <>
          <DataTable
            columns={columns}
            rows={data.items}
            empty={{
              title: moderation === 'pending' ? 'The queue is clear' : 'Nothing here',
              message: moderation === 'pending' ? 'No listings are waiting for review.' : 'Try another tab.'
            }}
          />
          <Pagination page={data.page} totalPages={data.totalPages} onChange={(next) => setParam({ page: next })} />
        </>
      ) : null}

      {pending ? (
        <ModerationModal
          property={pending.property}
          decision={pending.decision}
          onClose={() => setPending(null)}
          onDone={() => {
            setPending(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
};

export default AdminListings;
